import { Component } from "@angular/core";
import { SpecialUserPermissions } from "@app/enum/SpecialUserPermissions";
import { IAggregateFilterEvent, IFilterBarTab } from "@directives/_resourceFilters/filter-bar/filter-bar.models";
import { ReportTableRowDataSource } from "@reports/enums/ReportTableRowDataSource";
import { IReportHistory } from "@reports/models/IDynamicReportLog";
import { IReportDefinition, IReportDefinitionHistory } from "@reports/models/IReportDefinition";
import { DynamicReportService } from "@reports/_services/dynamicReports.service";
import { AccountService } from "@services/account.service";
import { AccountFeatureService } from "@services/accountFeature.service";
import { NavigationService } from "@services/navigation.service";
import { TranslationService } from "@services/translation.service";
import { lastValueFrom, Subject } from "rxjs";
import { take } from "rxjs/operators";
import { ReportsNavigationModule } from "../../reports.navigation";
import { IReportEditorSave } from "../../_components/report-editor/report-editor.component";
import { ReportHistoryService } from "../../_services/report-history.service";
import "./report-list-page.component.scss";

@Component({
    selector: "report-list-page",
    template: require("./report-list-page.component.html")
})
export class ReportListPageComponent {

    loading: boolean;

    allHistory: IReportDefinitionHistory[] = [];
    selectedCategoryHistory: IReportDefinitionHistory[] = [];
    displayReportHistory: IReportDefinitionHistory[] = [];

    definitions: IReportDefinition[];
    counts: number[] = [];

    filterTabs: IFilterBarTab<IReportDefinitionHistory>[] = [];


    // dialogs
    showScheduleReportDialog: boolean;
    reportEditorValid: boolean;
    showReportEditor: boolean;

    newReport: IReportDefinition;

    private destroy$: Subject<void> = new Subject();

    constructor(
        private readonly reportService: DynamicReportService,
        private readonly reportHistoryService: ReportHistoryService,
        private readonly translationService: TranslationService,
        private readonly navigationService: NavigationService,
        private readonly accountService: AccountService,
        private readonly accountFeatureService: AccountFeatureService) {

    }

    get userCanCreateReport(): boolean {
        return this.accountFeatureService.doesCurrentUserHaveSpecialPermission(SpecialUserPermissions.ManageReportBuilder);
    }

    ngOnInit(): void {
        this.createTabs();
        this.updateHistory(true);
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.unsubscribe();
    }

    private createTabs(): void {
        this.filterTabs = [];

        // Todo: Show hide based on account features
        this.createTabDefinition(this.translationService.translate("ReportListPage.AllTab"), (r) => true);
        this.createTabDefinition(this.translationService.translate("ReportListPage.CameraTab"), r => this.isReportOfType(r.definition, ["CameraDataSource"]));
        this.createTabDefinition(this.translationService.translate("ReportListPage.DriverTab"), r => this.filterDriverReports(r));
        this.createTabDefinition(this.translationService.translate("ReportListPage.VehicleTab"), r => this.filterVehicleReports(r));
        this.createTabDefinition(this.translationService.translate("ReportListPage.TMSTab"), r => this.isReportOfType(r.definition, ["TmsDataSource"]));
        this.createTabDefinition(this.translationService.translate("ReportListPage.TemperatureTab"), r => this.isReportOfType(r.definition, ["TemperatureDataSource", "TemperatureOutOfBoundsDataSource"]));
        this.createTabDefinition(this.translationService.translate("ReportListPage.BreathalockTab"), r => this.isReportOfType(r.definition, ["AlcolockDataSource"]));
        this.createTabDefinition(this.translationService.translate("ReportListPage.HealthCheckTab"), r => this.isReportOfType(r.definition, ["VehicleStatusDataSource"]));
    }

    onFilterBarChange(event: IAggregateFilterEvent<IReportDefinitionHistory>) {

        // We sometimes get weird events come through, need to understand why, until then this fixes a bug
        // This is LIKELY because of the default (change) subscription?
        if (!event.trigger) {
            return;
        }

        this.displayReportHistory = event.filteredData;
    }

    selectReportHistory(reportDefinition: IReportDefinition): void {
        this.showReport(reportDefinition.Id);
    }

    openReportEditor(): void {
        this.showReportEditor = true;
    }

    closeReportEditor(): void {
        this.showReportEditor = false;
    }

    onReportEditorValidated(valid: boolean) {
        this.reportEditorValid = valid;
    }

    onEditorSaved(reportSave: IReportEditorSave): void {
        this.closeReportEditor();

        if (!reportSave?.savedReport?.Id || reportSave.deleted) {
            this.updateHistory(true);
            return;
        }

        this.showReport(reportSave.savedReport.Id);
    }

    showReport(reportId: number): void {
        this.navigationService.go(ReportsNavigationModule.ViewReport, { reportId: reportId });
    }

    filterReportsBySearchText(r: IReportDefinitionHistory, text: string): boolean {
        if (r.definition.Title.toLocaleLowerCase().indexOf(text) >= 0) {
            return true;
        }

        return false;
    }

    // ---
    
    private updateHistory(showSpinner = false, invalidateCache = false): angular.IPromise<void> {

        this.loading = showSpinner;

        const definitionPromise = this.reportService.getReports(invalidateCache);
        const historyPromise = lastValueFrom(this.reportHistoryService.getLatestReportHistory().pipe(take(1))) as angular.IPromise<IReportHistory[]>;
        const countsPromise = this.reportService.getLogCounts(invalidateCache);
        const accountPromise = this.accountService.getAccountData();

        return this.reportService.$q
            .all([definitionPromise, historyPromise, countsPromise, accountPromise])
            .then(([definitions, history, counts, account]) => {

                this.definitions = definitions.filter(definition => this.accountFeatureService.isReportFeatureEnabledOnAccount(account, definition));
                this.counts = counts;

                this.allHistory = this.createReportHistory(this.definitions, history, this.counts);

                this.displayReportHistory = this.allHistory;
            })
            .finally(() => {
                this.loading = false;
            });
    }

    private createReportHistory(definitions: IReportDefinition[], logs: IReportHistory[], counts: {}): IReportDefinitionHistory[] {
        var reportHistory: IReportDefinitionHistory[] = [];

        definitions.forEach(definition => {
            if (!definition.Active) {
                return;
            }

            let history: IReportHistory[] = [];

            logs.forEach(reportLog => {
                if (definition.Id === reportLog.ReportDefinitionId) {
                    history.push(reportLog);
                }
            });

            reportHistory.push({
                definition: definition,
                history: history,
                latest: history[0],
                runCount: counts[definition.Id]
            });
        });

        reportHistory.sort(this.sortReportHistoryMethod);

        return reportHistory;
    }

    private sortReportHistoryMethod(x: IReportDefinitionHistory, y: IReportDefinitionHistory): number {
        if (!y.runCount) { return -1; }
        if (!x.runCount) { return 1; }

        return y.runCount - x.runCount;
    }

    private isReportOfType(report: IReportDefinition, dataSources: string[]): boolean {
        return report.TableDefinition.collatedHeaders.some(h => dataSources.some(source => source == h.headerName));
    }

    private filterDriverReports(r: IReportDefinitionHistory): boolean {
        return r.definition.RowDataSource == ReportTableRowDataSource.Driver;
    }

    private filterVehicleReports(r: IReportDefinitionHistory): boolean {
        var reportTypesToExclude = ["AlcolockDataSource", "VehicleStatusDataSource", "TemperatureDataSource", "TemperatureOutOfBoundsDataSource", "TmsDataSource", "CameraDataSource"];

        if (this.isReportOfType(r.definition, reportTypesToExclude)) {
            return false;
        }

        return r.definition.RowDataSource == ReportTableRowDataSource.Vehicle;
    }

    private createTabDefinition(translationString: string, callback: (report: IReportDefinitionHistory) => boolean) {
        this.filterTabs.push({ title: translationString, predicate: callback });
    }
}
